// FiscalDataCard — Story 10.9 (E10).
// Card de "Dados fiscais" no detalhe do cliente. Editor inline pro
// override permanente de CPF/CNPJ (DR-036), com validação + audit history.

import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, FileText, Save, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  formatBRDateTime,
  formatDocument,
  isValidCpfOrCnpj,
  normalizeDocument,
} from '@control-center/shared';

import { cn } from '@control-center/ui';

import { useUpdateClient } from '../../hooks/use-clients';
import type { ClientEntity } from '../../lib/clients-api';
import { extractErrorMessage } from '../../lib/errors';
import { useAuthStore } from '../../stores/useAuthStore';
import { Field, Input } from './form-primitives';
import { PillButton } from './PillButton';

export interface FiscalDataCardProps {
  client: ClientEntity;
}

export function FiscalDataCard({ client }: FiscalDataCardProps): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = role === 'admin' || role === 'financeiro';
  const update = useUpdateClient();

  const initialOverride = client.fiscalDocumentOverride ?? '';
  const [draftRaw, setDraftRaw] = useState(initialOverride);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const normalized = normalizeDocument(draftRaw);
  const principalDoc = client.document ? normalizeDocument(client.document) : '';

  const isEmpty = normalized.length === 0;
  const isDirty = normalized !== (initialOverride ? normalizeDocument(initialOverride) : '');
  const isSameAsPrincipal = !isEmpty && normalized === principalDoc;
  const isInvalid = !isEmpty && !isValidCpfOrCnpj(normalized);
  const canSave = canEdit && isDirty && !isInvalid && !isSameAsPrincipal && !isEmpty;
  const canClear = canEdit && !!initialOverride;

  const errorMessage = isInvalid
    ? 'CPF/CNPJ inválido.'
    : isSameAsPrincipal
      ? 'Igual ao documento principal — deixe vazio pra usar o padrão.'
      : undefined;

  async function handleSave(): Promise<void> {
    if (!canSave) return;
    try {
      await update.mutateAsync({
        id: client.id,
        payload: {
          fiscal_document_override: normalized,
          version: client.version,
        },
      });
      toast.success('Override fiscal salvo.', {
        description: 'Próximas NFs vão usar esse documento.',
      });
    } catch (err) {
      toast.error('Não conseguimos salvar.', {
        description: extractErrorMessage(err, 'Tente de novo.'),
      });
    }
  }

  async function handleClear(): Promise<void> {
    try {
      await update.mutateAsync({
        id: client.id,
        payload: {
          fiscal_document_override: null,
          version: client.version,
        },
      });
      setDraftRaw('');
      setConfirmClearOpen(false);
      toast.success('Override removido.', {
        description: `NFs voltam a usar ${formatDocument(client.document ?? '')}`,
      });
    } catch (err) {
      toast.error('Não conseguimos remover.', {
        description: extractErrorMessage(err, 'Tente de novo.'),
      });
    }
  }

  const setAt = client.fiscalDocumentOverrideSetAt;

  return (
    <div className="rounded-[14px] border border-line bg-panel p-5">
      <header className="mb-4 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-panel-3 text-muted">
          <FileText size={13} strokeWidth={1.8} />
        </span>
        <div>
          <h3 className="text-[13px] font-semibold text-ink">Dados fiscais</h3>
          <p className="text-[11px] text-muted-2">
            Documento alternativo pra emissão de NF (holdings, matriz/filial)
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field
          label="CPF/CNPJ preferencial pra NF"
          hint={
            !errorMessage
              ? `Principal: ${client.document ? formatDocument(client.document) : '—'}`
              : undefined
          }
          error={errorMessage}
        >
          {({ inputId }) => (
            <Input
              id={inputId}
              type="text"
              value={draftRaw}
              onChange={(e) => setDraftRaw(e.target.value)}
              placeholder="00.000.000/0000-00"
              disabled={!canEdit || update.isPending}
              hasError={Boolean(errorMessage)}
              tabular
            />
          )}
        </Field>

        <div className="flex flex-col justify-end gap-2">
          {setAt && (
            <p className="text-[11px] text-muted-2">
              Última mudança em{' '}
              <span className="text-muted" suppressHydrationWarning>
                {formatBRDateTime(setAt)}
              </span>
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <PillButton
              solid
              onClick={() => void handleSave()}
              disabled={!canSave || update.isPending}
              icon={<Save size={14} strokeWidth={2} />}
            >
              {update.isPending ? 'Salvando…' : 'Salvar override'}
            </PillButton>
            {canClear && (
              <PillButton
                danger
                onClick={() => setConfirmClearOpen(true)}
                disabled={update.isPending}
                icon={<Trash2 size={14} strokeWidth={2} />}
              >
                Remover
              </PillButton>
            )}
          </div>
        </div>
      </div>

      <ConfirmClearDialog
        open={confirmClearOpen}
        onOpenChange={setConfirmClearOpen}
        principalDoc={client.document ? formatDocument(client.document) : '—'}
        onConfirm={() => void handleClear()}
        isPending={update.isPending}
      />
    </div>
  );
}

/**
 * Story 10.9 AC10 — modal de confirmação ao limpar override.
 */
interface ConfirmClearDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  principalDoc: string;
  onConfirm: () => void;
  isPending: boolean;
}

function ConfirmClearDialog({
  open,
  onOpenChange,
  principalDoc,
  onConfirm,
  isPending,
}: ConfirmClearDialogProps): JSX.Element {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(440px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none',
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <Dialog.Title className="flex items-center gap-2 text-[16px] font-semibold tracking-[-0.005em] text-ink">
              <AlertTriangle size={16} strokeWidth={2} className="text-warn" />
              Remover override fiscal?
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <Dialog.Description className="text-[13px] leading-relaxed text-muted">
            Após remover, as próximas NFs deste cliente voltarão a usar o documento principal{' '}
            <strong className="tnum text-ink">{principalDoc}</strong>.
          </Dialog.Description>

          <div className="mt-5 flex items-center justify-end gap-2">
            <PillButton type="button" onClick={() => onOpenChange(false)} disabled={isPending}>
              Voltar
            </PillButton>
            <PillButton type="button" danger onClick={onConfirm} disabled={isPending}>
              {isPending ? 'Removendo…' : 'Confirmar remoção'}
            </PillButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
